// src/crm/applications/ApplicationsPage.tsx
import { useState } from 'react'
import { useAppStore } from '../../shell/appStore'
import { useApplications, useAcceptApplication, usePatchApplicationStatus } from '../hooks/useApplications'
import type { Application } from '../../lib/types'

const STATUS_COLORS: Record<Application['status'], string> = {
  new: 'var(--bronze)',
  reviewed: '#2A678D',
  accepted: '#4a9a5c',
  rejected: 'var(--dim)',
}

const td: React.CSSProperties = {
  padding: '0.625rem 0.75rem',
  fontSize: '0.875rem',
  borderBottom: '1px solid var(--line)',
  verticalAlign: 'middle',
}

const th: React.CSSProperties = {
  padding: '0.5rem 0.75rem',
  textAlign: 'left',
  fontSize: '0.75rem',
  fontWeight: 600,
  color: 'var(--dim)',
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  borderBottom: '1px solid var(--line)',
  whiteSpace: 'nowrap',
}

function StatusBadge({ status }: { status: Application['status'] }) {
  return (
    <span
      data-testid="application-status-badge"
      style={{
        fontSize: '0.75rem',
        fontWeight: 600,
        color: STATUS_COLORS[status],
        background: 'var(--raised)',
        border: `1px solid ${STATUS_COLORS[status]}40`,
        borderRadius: '4px',
        padding: '0.125rem 0.5rem',
      }}
    >
      {status}
    </span>
  )
}

function ApplicationRow({ app }: { app: Application }) {
  const accept = useAcceptApplication()
  const patchStatus = usePatchApplicationStatus()
  const [confirmingAccept, setConfirmingAccept] = useState(false)

  function handleAcceptClick() {
    setConfirmingAccept(true)
  }

  function handleConfirmAccept() {
    setConfirmingAccept(false)
    accept.mutate(app.id)
  }

  function handleCancelAccept() {
    setConfirmingAccept(false)
  }

  function handleReject() {
    patchStatus.mutate({ id: app.id, status: 'rejected' })
  }

  function handleMarkReviewed() {
    patchStatus.mutate({ id: app.id, status: 'reviewed' })
  }

  const isPending = accept.isPending || patchStatus.isPending

  return (
    <tr>
      <td style={td}>
        <span style={{ fontWeight: 500, color: 'var(--fg)' }}>{app.name}</span>
      </td>
      <td style={td}>
        {app.email ? (
          <a href={`mailto:${app.email}`} style={{ color: 'var(--bronze)', fontSize: '0.875rem' }}>
            {app.email}
          </a>
        ) : (
          <span style={{ color: 'var(--dim)' }}>—</span>
        )}
      </td>
      <td style={td}>
        <StatusBadge status={app.status} />
      </td>
      <td style={td}>
        <span style={{ color: 'var(--muted)', fontSize: '0.8125rem' }}>
          {new Date(app.createdAt).toLocaleDateString('fr-FR')}
        </span>
      </td>
      <td style={td} onClick={(e) => e.stopPropagation()}>
        {confirmingAccept ? (
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <span style={{ fontSize: '0.75rem', color: 'var(--muted)' }}>
              Confirmer ?
            </span>
            <button
              onClick={handleConfirmAccept}
              disabled={isPending}
              style={{
                background: 'rgba(74,154,92,0.12)',
                border: '1px solid rgba(74,154,92,0.3)',
                borderRadius: '5px',
                padding: '0.25rem 0.5rem',
                color: '#4a9a5c',
                fontSize: '0.75rem',
                cursor: isPending ? 'not-allowed' : 'pointer',
                opacity: isPending ? 0.6 : 1,
                fontFamily: 'inherit',
              }}
            >
              Confirmer
            </button>
            <button
              onClick={handleCancelAccept}
              style={{
                background: 'none',
                border: '1px solid var(--line)',
                borderRadius: '5px',
                padding: '0.25rem 0.5rem',
                color: 'var(--muted)',
                fontSize: '0.75rem',
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              Annuler
            </button>
          </div>
        ) : (
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            {app.status === 'new' && (
              <button
                onClick={handleMarkReviewed}
                disabled={isPending}
                style={{
                  background: 'var(--elevated)',
                  border: '1px solid var(--line)',
                  borderRadius: '5px',
                  padding: '0.25rem 0.5rem',
                  color: 'var(--muted)',
                  fontSize: '0.75rem',
                  cursor: isPending ? 'not-allowed' : 'pointer',
                  opacity: isPending ? 0.6 : 1,
                  fontFamily: 'inherit',
                }}
              >
                Examiner
              </button>
            )}
            {(app.status === 'new' || app.status === 'reviewed') && (
              <>
                <button
                  onClick={handleAcceptClick}
                  disabled={isPending}
                  style={{
                    background: 'rgba(74,154,92,0.12)',
                    border: '1px solid rgba(74,154,92,0.3)',
                    borderRadius: '5px',
                    padding: '0.25rem 0.5rem',
                    color: '#4a9a5c',
                    fontSize: '0.75rem',
                    cursor: isPending ? 'not-allowed' : 'pointer',
                    opacity: isPending ? 0.6 : 1,
                    fontFamily: 'inherit',
                  }}
                >
                  Accepter
                </button>
                <button
                  onClick={handleReject}
                  disabled={isPending}
                  style={{
                    background: 'rgba(217,83,30,0.12)',
                    border: '1px solid rgba(217,83,30,0.25)',
                    borderRadius: '5px',
                    padding: '0.25rem 0.5rem',
                    color: 'var(--arancio)',
                    fontSize: '0.75rem',
                    cursor: isPending ? 'not-allowed' : 'pointer',
                    opacity: isPending ? 0.6 : 1,
                    fontFamily: 'inherit',
                  }}
                >
                  Rejeter
                </button>
              </>
            )}
          </div>
        )}
      </td>
    </tr>
  )
}

export function ApplicationsPage() {
  const { activeAppId } = useAppStore()
  const { data: applications = [], isLoading, isError } = useApplications(activeAppId ?? '')

  if (!activeAppId) {
    return (
      <div style={{ padding: '2rem 2.5rem', color: 'var(--muted)' }}>
        Sélectionnez une application pour voir les candidatures.
      </div>
    )
  }

  return (
    <div style={{ padding: '2rem 2.5rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
        <h1 style={{ fontSize: '1.5rem', fontWeight: 600, color: 'var(--fg)', margin: 0, letterSpacing: '-0.025em' }}>
          Candidatures
        </h1>
        <span style={{ fontSize: '0.8125rem', color: 'var(--dim)' }}>
          {applications.length} candidature{applications.length > 1 ? 's' : ''}
        </span>
      </div>

      {isLoading && (
        <p style={{ color: 'var(--muted)', padding: '2rem 0' }}>Chargement…</p>
      )}
      {isError && (
        <p style={{ color: 'var(--arancio)', padding: '2rem 0' }}>
          Erreur lors du chargement des candidatures.
        </p>
      )}

      {!isLoading && !isError && (
        <div style={{
          background: 'var(--surface)',
          border: '1px solid var(--line)',
          borderRadius: '10px',
          overflow: 'hidden',
        }}>
          {applications.length === 0 ? (
            <div style={{ padding: '3rem 2rem', textAlign: 'center', color: 'var(--muted)' }}>
              <p style={{ margin: 0 }}>Aucune candidature pour le moment.</p>
              <p style={{ margin: '0.5rem 0 0', fontSize: '0.8125rem', color: 'var(--dim)' }}>
                Partagez le lien /apply/{activeAppId} pour recevoir des candidatures.
              </p>
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={th}>Nom</th>
                  <th style={th}>Email</th>
                  <th style={th}>Statut</th>
                  <th style={th}>Date</th>
                  <th style={th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {applications.map((app) => (
                  <ApplicationRow key={app.id} app={app} />
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  )
}
